import { useEffect, useState } from 'react';

type Theme = 'dark' | 'light' | 'system';

function getSystemTheme(): 'dark' | 'light' {
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function applyThemeClass(theme: Theme): void {
  const resolved = theme === 'system' ? getSystemTheme() : theme;
  document.documentElement.classList.toggle('dark', resolved === 'dark');
}

/** Persisted theme choice; browser tests set it directly to capture both themes. */
export const THEME_STORAGE_KEY = 'contract-analyzer-theme';

export function useTheme() {
  const [theme, setThemeState] = useState<Theme>(() => {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    return (stored === 'dark' || stored === 'light' || stored === 'system') ? stored : 'light';
  });

  useEffect(() => {
    applyThemeClass(theme);
    localStorage.setItem(THEME_STORAGE_KEY, theme);
  }, [theme]);

  useEffect(() => {
    if (theme !== 'system') return;

    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = () => { applyThemeClass('system'); };
    mql.addEventListener('change', handler);
    return () => { mql.removeEventListener('change', handler); };
  }, [theme]);

  function setTheme(next: Theme) {
    setThemeState(next);
  }

  return { setTheme, theme } as const;
}
